import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Snackbar,
  MenuItem,
  Grid,
  InputAdornment,
  Chip,
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, api } from "../context/AuthContext";
import { IconButton } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const NAVY = "#1E3A8A";
const NAVY_GRADIENT = "linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)";

const cardSx = {
  p: "28px",
  borderRadius: "20px",
  border: "1px solid #E5E7EB",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
  backgroundColor: "#FFFFFF",
  mb: 3,
};

const textInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    height: "50px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#BFDBFE" },
    "&.Mui-focused fieldset": { borderColor: NAVY, borderWidth: "1.5px" },
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(30,58,138,0.08)" },
  },
  "& .MuiInputLabel-root": { color: "#9CA3AF", fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: NAVY },
};

const selectInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#BFDBFE" },
    "&.Mui-focused fieldset": { borderColor: NAVY, borderWidth: "1.5px" },
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(30,58,138,0.08)" },
  },
  "& .MuiSelect-select": { py: "13px" },
  "& .MuiInputLabel-root": { color: "#9CA3AF", fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: NAVY },
};

const multilineInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#BFDBFE" },
    "&.Mui-focused fieldset": { borderColor: NAVY, borderWidth: "1.5px" },
    "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(30,58,138,0.08)" },
  },
  "& .MuiInputLabel-root": { color: "#9CA3AF", fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: NAVY },
};

function FieldLabel({ children, required }) {
  return (
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: "#374151", mb: 0.8, fontSize: "13px" }}
    >
      {children}
      {required && <span style={{ color: "#EF4444", marginLeft: 2 }}>*</span>}
    </Typography>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          backgroundColor: "#EFF6FF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: NAVY,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{ fontWeight: 700, color: "#111827", fontSize: "15px", lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "#6B7280", lineHeight: 1.4 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function PostJob() {
  const { user, setUser } = useAuth();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    companyName: "",
    location: "",
    workMode: "",
    salary: "",
    stipend: "",
    minExperience: "",
    maxExperience: "",
    requirements: "",
    jobType: "technical",
  });
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [quillKey, setQuillKey] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const navigate = useNavigate();
  const [showLimitPopup, setShowLimitPopup] = useState(false);
  const [limitMessage, setLimitMessage] = useState("");
  const [screeningQuestions, setScreeningQuestions] = useState([""]);
  const [limitPlanType, setLimitPlanType] = useState("");

  const handleUpgrade = () => {
    setShowLimitPopup(false);
    navigate("/pricing");
  };

  const [usage, setUsage] = useState({ jobs: 0, applications: 0 });

  const fetchUsage = async () => {
    if (!user) return;
    try {
      if (user.plan === "free_trial" && (user.job_limit || 0) <= 0) {
        setError(
          <Box>
            <Typography variant="body1">
              Free Trial job posting limit reached. Please upgrade your plan.{" "}
              <Link to="/pricing" style={{ color: "inherit", fontWeight: "bold" }}>
                Upgrade your plan
              </Link>
            </Typography>
          </Box>
        );
      }
    } catch (err) {
      console.error("Failed to fetch usage info:", err);
    }
  };

  useEffect(() => {
    console.log("USER DATA:", user);
    console.log("remaining_custom_jobs:", user?.remaining_custom_jobs);
    console.log("has_active_subscription:", user?.has_active_subscription);

    const hasSubscription =
      user?.has_active_subscription === true ||
      user?.has_active_subscription === 1 ||
      user?.has_active_subscription === "true";

    const hasCustomCredits = (user?.remaining_custom_jobs || 0) > 0;

    if (
      user &&
      !hasSubscription &&
      !hasCustomCredits &&
      user.role !== "super_admin"
    ) {
      setError(
        <Box>
          <Typography variant="body1">
            You need an active subscription to post jobs.{" "}
            <Link to="/pricing" style={{ color: "inherit", fontWeight: "bold" }}>
              Upgrade your plan
            </Link>
          </Typography>
        </Box>
      );
    } else if (user?.plan === "free_trial") {
      fetchUsage();
    }
  }, [user]);

  function handleChange(e) {
    if (e && e.target) {
      setFormData({ ...formData, [e.target.name]: e.target.value });
      if (fieldErrors[e.target.name]) {
        setFieldErrors({ ...fieldErrors, [e.target.name]: "" });
      }
    }
  }

  function handleQuillChange(value) {
    setFormData({ ...formData, description: value });
    if (fieldErrors.description) {
      setFieldErrors({ ...fieldErrors, description: "" });
    }
  }

  const handleScreeningQuestionChange = (index, value) => {
    const updated = [...screeningQuestions];
    updated[index] = value;
    setScreeningQuestions(updated);
  };

  const addScreeningQuestion = () => {
    if (screeningQuestions.length < 6) {
      setScreeningQuestions([...screeningQuestions, ""]);
    }
  };

  const removeScreeningQuestion = (index) => {
    const updated = screeningQuestions.filter((_, i) => i !== index);
    setScreeningQuestions(updated.length ? updated : [""]);
  };

  const getWordCount = (html = "") => {
    const text = html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").trim();
    if (!text) return 0;
    return text.split(/\s+/).filter(Boolean).length;
  };

  const validateJobForm = () => {
    let newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Job Title is required";
    if (!formData.companyName.trim()) newErrors.companyName = "Company Name is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.salary.trim()) newErrors.salary = "Salary Range is required";
    if (formData.minExperience === "" || formData.maxExperience === "") {
      newErrors.experienceRange = "Experience range is required";
    } else if (Number(formData.minExperience) > Number(formData.maxExperience)) {
      newErrors.experienceRange =
        "Minimum experience cannot be greater than maximum experience";
    }
    if (!formData.description.trim()) newErrors.description = "Job Description is required";
    if (!formData.requirements.trim()) newErrors.requirements = "Requirements are required";
    return newErrors;
  };

  const handlePreview = (e) => {
    e.preventDefault();
    setError("");
    const newErrors = validateJobForm();
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return;
    }
    setPreviewOpen(true);
  };

  const handleConfirmPost = async () => {
    setPreviewOpen(false);
    await handleSubmit({ preventDefault: () => {} });
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    let newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Job Title is required";
    if (!formData.companyName.trim()) newErrors.companyName = "Company Name is required";
    if (!formData.location.trim()) newErrors.location = "Location is required";
    if (!formData.salary.trim()) newErrors.salary = "Salary Range is required";
    if (formData.minExperience === "" || formData.maxExperience === "") {
      newErrors.experienceRange = "Experience range is required";
    } else if (Number(formData.minExperience) > Number(formData.maxExperience)) {
      newErrors.experienceRange =
        "Minimum experience cannot be greater than maximum experience";
    }
    if (!formData.description.trim()) newErrors.description = "Job Description is required";
    if (!formData.requirements.trim()) newErrors.requirements = "Requirements are required";

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      return;
    }

    const hasSubscription =
      user?.has_active_subscription === true ||
      user?.has_active_subscription === 1 ||
      user?.has_active_subscription === "true";

    const isFreeTrialExpired =
      user?.plan === "free_trial" &&
      user?.free_trial_expires_at &&
      new Date(user.free_trial_expires_at) < new Date();

    const isOverLimit =
      user?.plan === "free_trial" &&
      ((user.job_limit || 0) <= 0 || isFreeTrialExpired);

    const hasCustomCredits = (user?.remaining_custom_jobs || 0) > 0;

    if (!hasSubscription && !hasCustomCredits && user?.role !== "super_admin") {
      setError(
        <Box>
          <Typography variant="body1">
            {isFreeTrialExpired
              ? "Your free trial has expired. "
              : "Your free trial is completed. "}
            <Link to="/pricing" style={{ color: "inherit", fontWeight: "bold" }}>
              Upgrade your plan
            </Link>
          </Typography>
        </Box>
      );
      return;
    }

    setLoading(true);
    setSuccess(false);
    setSuccessMessage("");

    try {
      const jobData = {
        ...formData,
        work_mode: formData.workMode,
        experienceRange: `${formData.minExperience}-${formData.maxExperience}`,
        requirements: formData.requirements.split("\n").filter((r) => r.trim()),
        screening_questions: screeningQuestions.filter((q) => q.trim() !== ""),
      };

      console.log("========== POST JOB DEBUG ==========");
      console.log("USER PLAN:", user?.plan);
      console.log("PROF OVERAGE REMAINING:", user?.professional_overage_jobs);
      console.log("STARTER OVERAGE REMAINING:", user?.overage_jobs);
      console.log("HAS SUBSCRIPTION:", user?.has_active_subscription);
      console.log("POSTING JOB NOW...");

      const response = await api.post("/admin/jobs", jobData);

      console.log("JOB CREATED SUCCESSFULLY:", response.data);
      console.log("===================================");

      try {
        const userRes = await api.get("/auth/user");
        setUser(userRes.data);
      } catch (uErr) {
        console.error("Failed to refresh user state:", uErr);
      }

      setFormData({
        title: "",
        description: "",
        companyName: "",
        location: "",
        workMode: "",
        salary: "",
        stipend: "",
        minExperience: "",
        maxExperience: "",
        requirements: "",
        jobType: "technical",
      });
      setScreeningQuestions([""]);
      setFieldErrors({});
      setQuillKey((k) => k + 1);

      setSuccess(true);
      setSuccessMessage("Your Job Has Been Posted Successfully.");
      setError("");

      if (user?.plan === "free_trial") {
        fetchUsage();
        try {
          const userRes = await api.get("/auth/user");
          setUser(userRes.data);
        } catch (uErr) {
          console.error("Failed to refresh user state:", uErr);
        }
      }
    } catch (err) {
      console.log("========== JOB ERROR DEBUG ==========");
      console.log("STATUS:", err.response?.status);
      console.log("FULL RESPONSE:", err.response?.data);
      console.log("MESSAGE:", err.response?.data?.message);
      console.log("PLAN TYPE:", err.response?.data?.planType);
      console.log("SHOW BUY EXTRA:", err.response?.data?.showBuyExtra);
      console.log("=====================================");
      console.error("[DEBUG] Final API failure:", err);

      if (err.response?.status === 403) {
        const planType = err.response?.data?.planType;
        const backendMessage = err.response?.data?.message || "Limit reached.";

        if (planType === "payg") {
          setError(backendMessage);
          setShowLimitPopup(false);
          return;
        }

        setLimitMessage(backendMessage);
        setLimitPlanType(planType || "");
        setShowLimitPopup(true);
        return;
      }

      const errorMessage = err.response?.data?.message || "Failed to create job.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC" }}>
      <Container maxWidth="lg" sx={{ py: 5 }}>

        {/* Alerts */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: "12px", "& .MuiAlert-message": { width: "100%" } }}
          >
            {error}
            {showLimitPopup && user?.plan !== "payg" && (
              <Box mt={1}>
                <Button size="small" variant="contained" onClick={handleUpgrade}>
                  Upgrade Plan
                </Button>
              </Box>
            )}
          </Alert>
        )}

        {user?.plan === "free_trial" && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: "12px" }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Free Trial Usage:
            </Typography>
            <Typography variant="body2">
              Jobs Remaining: {user.job_limit ?? 1}
            </Typography>
            <Typography variant="body2">
              Applications Remaining: {user.application_limit ?? 5}
            </Typography>
            {user?.free_trial_expires_at && (
              <Typography variant="body2" color="error" fontWeight="bold">
                Free Trial Expires:{" "}
                {new Date(user.free_trial_expires_at).toLocaleString()}
              </Typography>
            )}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: "12px" }}
            onClose={() => {
              setSuccess(false);
              setSuccessMessage("");
            }}
          >
            {successMessage}
          </Alert>
        )}

        {/* Page Header */}
        <Box sx={{ mb: 4, display: "flex", alignItems: "flex-start", gap: 2.5 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "16px",
              backgroundColor: "#EFF6FF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              border: "1px solid #DBEAFE",
            }}
          >
            <WorkOutlineOutlinedIcon sx={{ fontSize: 32, color: NAVY }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: NAVY,
                fontSize: { xs: "22px", md: "28px" },
                lineHeight: 1.2,
              }}
            >
              Post a New Job
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#6B7280", mt: 0.5, fontSize: "15px" }}
            >
              Fill in the details below to create and publish a new job listing.
            </Typography>
          </Box>
        </Box>

        {/* Form */}
        <form onSubmit={handlePreview} noValidate>
          {(() => {
            const hasSubscription =
              user?.has_active_subscription === true ||
              user?.has_active_subscription === 1 ||
              user?.has_active_subscription === "true";

            const isFreeTrialExpired =
              user?.plan === "free_trial" &&
              user?.free_trial_expires_at &&
              new Date(user.free_trial_expires_at) < new Date();

            const isFreeTrialOverLimit =
              user?.plan === "free_trial" &&
              ((user?.job_limit || 0) <= 0 || isFreeTrialExpired);

            const hasCustomCredits = (user?.remaining_custom_jobs || 0) > 0;

            const canPost =
              user?.role === "super_admin" ||
              (hasSubscription && !isFreeTrialOverLimit) ||
              hasCustomCredits;

            const isBPO = formData.jobType === "non_technical_bpo";

            const BPO_SCREENING_EXAMPLES = [
              "e.g. Are you comfortable working in rotational or night shifts?",
              "e.g. How do you handle an angry or dissatisfied customer?",
              "e.g. Do you have prior experience in voice or non-voice BPO processes?",
              "e.g. Rate your English communication skills on a scale of 1 to 10.",
              "e.g. Are you open to work-from-office requirements?",
              "e.g. What is your expected CTC and notice period?",
            ];

            return (
              <>
                {/* ── Card 0: Job Type Selection ── */}
                <Paper sx={cardSx}>
                  <SectionHeader
                    icon={<WorkOutlineOutlinedIcon fontSize="small" />}
                    title="Job Type"
                    subtitle="Select the category that best describes this position"
                  />
                  <Grid container spacing={2}>
                    {[
                      {
                        value: "technical",
                        label: "Technical",
                        description:
                          "For Software, IT, Engineering and Technical roles.",
                        icon: (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="16 18 22 12 16 6" />
                            <polyline points="8 6 2 12 8 18" />
                          </svg>
                        ),
                      },
                      {
                        value: "non_technical_bpo",
                        label: "Non-Technical (BPO)",
                        description:
                          "For Customer Support, Voice, Non-Voice, Sales, Operations and BPO roles.",
                        icon: (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.81 19.79 19.79 0 01.01 2.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14v2.92z" />
                          </svg>
                        ),
                      },
                    ].map((option) => {
                      const selected = formData.jobType === option.value;
                      return (
                        <Grid item xs={12} sm={6} key={option.value}>
                          <Box
                            onClick={() =>
                              canPost &&
                              setFormData({ ...formData, jobType: option.value })
                            }
                            sx={{
                              p: 2.5,
                              borderRadius: "14px",
                              border: selected
                                ? `2px solid ${NAVY}`
                                : "2px solid #E5E7EB",
                              backgroundColor: selected ? "#EFF6FF" : "#FAFAFA",
                              cursor: canPost ? "pointer" : "default",
                              transition: "all 0.18s ease",
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                              "&:hover": canPost
                                ? {
                                    borderColor: selected ? NAVY : "#BFDBFE",
                                    backgroundColor: selected ? "#EFF6FF" : "#F5F9FF",
                                  }
                                : {},
                            }}
                          >
                            <Box
                              sx={{
                                mt: 0.3,
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                border: selected
                                  ? `6px solid ${NAVY}`
                                  : "2px solid #D1D5DB",
                                flexShrink: 0,
                                transition: "all 0.15s ease",
                              }}
                            />
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "10px",
                                backgroundColor: selected ? "#DBEAFE" : "#E5E7EB",
                                color: selected ? NAVY : "#6B7280",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                transition: "all 0.15s ease",
                              }}
                            >
                              {option.icon}
                            </Box>
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "14px",
                                  color: selected ? NAVY : "#111827",
                                  lineHeight: 1.3,
                                  mb: 0.5,
                                }}
                              >
                                {option.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: selected ? "#1E40AF" : "#6B7280",
                                  lineHeight: 1.5,
                                  display: "block",
                                }}
                              >
                                {option.description}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>

                {/* ── Card 1: Job Details ── */}
                <Paper sx={cardSx}>
                  <SectionHeader
                    icon={<WorkOutlineOutlinedIcon fontSize="small" />}
                    title="Job Details"
                    subtitle="Basic information about the position"
                  />
                  <Grid container spacing={2.5}>

                    {/* Job Title */}
                    <Grid item xs={12}>
                      <FieldLabel required>Job Title</FieldLabel>
                      <TextField
                        fullWidth
                        placeholder={isBPO ? "e.g. Customer Support Executive" : "e.g. Senior React Developer"}
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        disabled={!canPost}
                        error={!!fieldErrors.title}
                        helperText={fieldErrors.title}
                        inputProps={{ "data-allow-paste": "true" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <WorkOutlineOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={textInputSx}
                      />
                    </Grid>

                    {/* Company Name */}
                    <Grid item xs={12} sm={6}>
                      <FieldLabel required>Company Name</FieldLabel>
                      <TextField
                        fullWidth
                        placeholder="e.g. Acme Corporation"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        disabled={!canPost}
                        error={!!fieldErrors.companyName}
                        helperText={fieldErrors.companyName}
                        inputProps={{ "data-allow-paste": "true" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={textInputSx}
                      />
                    </Grid>

                    {/* Location */}
                    <Grid item xs={12} sm={6}>
                      <FieldLabel required>Location</FieldLabel>
                      <TextField
                        fullWidth
                        placeholder="e.g. Bangalore, India"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        disabled={!canPost}
                        error={!!fieldErrors.location}
                        helperText={fieldErrors.location}
                        inputProps={{ "data-allow-paste": "true" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOnOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={textInputSx}
                      />
                    </Grid>

                    {/* Work Mode */}
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Work Mode</FieldLabel>
                      <TextField
                        fullWidth
                        select
                        name="workMode"
                        value={formData.workMode}
                        onChange={handleChange}
                        disabled={!canPost}
                        sx={selectInputSx}
                      >
                        <MenuItem value=""><em>Select work mode</em></MenuItem>
                        <MenuItem value="office">Work From Office</MenuItem>
                        <MenuItem value="hybrid">Hybrid</MenuItem>
                        <MenuItem value="remote">Remote</MenuItem>
                      </TextField>
                    </Grid>

                    {/* Salary Range */}
                    <Grid item xs={12} sm={6}>
                      <FieldLabel required>Salary Range</FieldLabel>
                      <TextField
                        fullWidth
                        placeholder={isBPO ? "e.g. ₹2.5L – ₹5L per annum" : "e.g. ₹8L – ₹14L per annum"}
                        name="salary"
                        value={formData.salary}
                        onChange={handleChange}
                        disabled={!canPost}
                        error={!!fieldErrors.salary}
                        helperText={fieldErrors.salary}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PaymentsOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={textInputSx}
                      />
                    </Grid>

                    {/* Stipend */}
                    <Grid item xs={12} sm={6}>
                      <FieldLabel>Stipend</FieldLabel>
                      <TextField
                        fullWidth
                        select
                        name="stipend"
                        value={formData.stipend}
                        onChange={handleChange}
                        disabled={!canPost}
                        sx={selectInputSx}
                      >
                        <MenuItem value=""><em>Select stipend</em></MenuItem>
                        <MenuItem value="Unpaid">Unpaid</MenuItem>
                        <MenuItem value="0–10k">0–10k</MenuItem>
                        <MenuItem value="10k–20k">10k–20k</MenuItem>
                        <MenuItem value="20k–30k">20k–30k</MenuItem>
                        <MenuItem value="30k–40k">30k–40k</MenuItem>
                        <MenuItem value="40k–50k">40k–50k</MenuItem>
                        <MenuItem value="50k and above">50k and above</MenuItem>
                      </TextField>
                    </Grid>

                    {/* Experience Range */}
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2.5,
                          borderRadius: "12px",
                          backgroundColor: "#F8FAFC",
                          border: "1px solid #E5E7EB",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                          <WorkHistoryOutlinedIcon sx={{ fontSize: 16, color: NAVY }} />
                          <Typography
                            sx={{ fontWeight: 600, color: "#374151", fontSize: "13px" }}
                          >
                            Experience Range <span style={{ color: "#EF4444" }}>*</span>
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FieldLabel>Minimum Experience (Years)</FieldLabel>
                            <TextField
                              fullWidth
                              select
                              name="minExperience"
                              value={formData.minExperience}
                              onChange={handleChange}
                              disabled={!canPost}
                              error={!!fieldErrors.experienceRange}
                              helperText={fieldErrors.experienceRange}
                              sx={selectInputSx}
                            >
                              <MenuItem value=""><em>Select minimum</em></MenuItem>
                              {[...Array(21)].map((_, i) => (
                                <MenuItem key={i} value={i}>
                                  {i} {i === 1 ? "Year" : "Years"}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FieldLabel>Maximum Experience (Years)</FieldLabel>
                            <TextField
                              fullWidth
                              select
                              name="maxExperience"
                              value={formData.maxExperience}
                              onChange={handleChange}
                              disabled={!canPost}
                              error={!!fieldErrors.experienceRange}
                              sx={selectInputSx}
                            >
                              <MenuItem value=""><em>Select maximum</em></MenuItem>
                              {[...Array(21)].map((_, i) => (
                                <MenuItem key={i} value={i}>
                                  {i} {i === 1 ? "Year" : "Years"}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        </Grid>
                        {formData.minExperience !== "" && formData.maxExperience !== "" && (
                          <Box
                            sx={{
                              mt: 1.5,
                              px: 2,
                              py: 0.8,
                              borderRadius: "8px",
                              backgroundColor: "#EFF6FF",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.8,
                            }}
                          >
                            <WorkHistoryOutlinedIcon sx={{ fontSize: 14, color: NAVY }} />
                            <Typography variant="caption" sx={{ color: NAVY, fontWeight: 600 }}>
                              {formData.minExperience} – {formData.maxExperience} Years Required
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* ── Card 2: Job Description ── */}
                <Paper sx={cardSx}>
                  <SectionHeader
                    icon={<DescriptionOutlinedIcon fontSize="small" />}
                    title="Job Description"
                    subtitle="Describe the role, responsibilities, and expectations"
                  />
                  <Box
                    sx={{
                      border: "1.5px solid #E5E7EB",
                      borderRadius: "10px",
                      overflow: "hidden",
                      backgroundColor: "#fff",
                      "&:focus-within": {
                        borderColor: NAVY,
                        boxShadow: "0 0 0 3px rgba(30,58,138,0.08)",
                      },
                      "& .ql-toolbar": {
                        borderBottom: "1px solid #E5E7EB !important",
                        borderTop: "none !important",
                        borderLeft: "none !important",
                        borderRight: "none !important",
                        backgroundColor: "#FAFAFA",
                        padding: "8px 12px !important",
                      },
                      "& .ql-container": {
                        borderTop: "none !important",
                        borderBottom: "none !important",
                        borderLeft: "none !important",
                        borderRight: "none !important",
                        fontSize: "14px",
                      },
                    }}
                    data-allow-paste="true"
                  >
                    <ReactQuill
                      key={quillKey}
                      theme="snow"
                      value={formData.description}
                      onChange={handleQuillChange}
                      disabled={!canPost}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, false] }],
                          ["bold", "italic"],
                          [{ list: "ordered" }, { list: "bullet" }],
                          ["clean"],
                        ],
                      }}
                      style={{ height: "220px" }}
                      placeholder={isBPO
                        ? "Describe the role, shift timings, daily responsibilities, KPIs, and performance expectations for this BPO position..."
                        : "Describe the job role, responsibilities, and expectations..."}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 1.5,
                    }}
                  >
                    {fieldErrors.description ? (
                      <Typography variant="caption" color="error">
                        {fieldErrors.description}
                      </Typography>
                    ) : (
                      <Box />
                    )}
                    <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                      {getWordCount(formData.description)} words
                    </Typography>
                  </Box>
                </Paper>

                {/* ── Card 3: Requirements ── */}
                <Paper sx={cardSx}>
                  <SectionHeader
                    icon={<AssignmentTurnedInOutlinedIcon fontSize="small" />}
                    title="Requirements"
                    subtitle="List each requirement on a new line"
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    placeholder={isBPO
                      ? "e.g.\n1+ year of experience in customer support or BPO\nExcellent verbal and written English communication skills\nAbility to work in rotational or night shifts\nStrong problem-solving and active listening skills\nFamiliarity with CRM tools and ticketing systems"
                      : "e.g.\n3+ years of experience in React.js\nStrong knowledge of REST APIs\nExcellent communication skills"}
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleChange}
                    disabled={!canPost}
                    error={!!fieldErrors.requirements}
                    helperText={fieldErrors.requirements || "Enter each requirement on a new line"}
                    inputProps={{ "data-allow-paste": "true" }}
                    sx={{
                      ...multilineInputSx,
                      "& .MuiInputBase-input": {
                        whiteSpace: "pre-line",
                        wordBreak: "break-word",
                      },
                    }}
                  />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                      {formData.requirements.length} characters
                    </Typography>
                  </Box>
                </Paper>

                {/* ── Card 4: Screening Questions ── */}
                <Paper sx={cardSx}>
                  <SectionHeader
                    icon={<HelpOutlineIcon fontSize="small" />}
                    title="Screening Questions"
                    subtitle="Add up to 6 short questions for candidates (optional)"
                  />

                  {screeningQuestions.map((question, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: index < screeningQuestions.length - 1 ? 2 : 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          backgroundColor: "#EFF6FF",
                          border: "1px solid #DBEAFE",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: NAVY }}>
                          {index + 1}
                        </Typography>
                      </Box>
                      <TextField
                        fullWidth
                        placeholder={isBPO
                          ? (BPO_SCREENING_EXAMPLES[index] || `Enter question ${index + 1}`)
                          : `Enter question ${index + 1}`}
                        value={question}
                        onChange={(e) => handleScreeningQuestionChange(index, e.target.value)}
                        disabled={!canPost}
                        inputProps={{ "data-allow-paste": "true" }}
                        sx={textInputSx}
                      />
                      {screeningQuestions.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeScreeningQuestion(index)}
                          disabled={!canPost}
                          sx={{
                            color: "#EF4444",
                            backgroundColor: "#FEF2F2",
                            "&:hover": { backgroundColor: "#FEE2E2" },
                            flexShrink: 0,
                            width: 34,
                            height: 34,
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  ))}

                  {screeningQuestions.length < 6 && (
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={addScreeningQuestion}
                      disabled={!canPost}
                      startIcon={<AddIcon />}
                      sx={{
                        mt: 1,
                        borderRadius: "10px",
                        py: 1.4,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "14px",
                        borderColor: "#E5E7EB",
                        borderStyle: "dashed",
                        color: NAVY,
                        "&:hover": {
                          borderColor: NAVY,
                          backgroundColor: "#EFF6FF",
                          borderStyle: "dashed",
                        },
                      }}
                    >
                      Add Screening Question
                    </Button>
                  )}
                </Paper>

                {/* ── Bottom Actions ── */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 2,
                    pb: 4,
                  }}
                >
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate("/admin/dashboard")}
                    sx={{
                      borderRadius: "10px",
                      px: 3.5,
                      py: 1.3,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "14px",
                      borderColor: "#E5E7EB",
                      color: "#374151",
                      "&:hover": {
                        borderColor: "#D1D5DB",
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<VisibilityOutlinedIcon />}
                    disabled={loading || !canPost}
                    sx={{
                      borderRadius: "10px",
                      px: 3.5,
                      py: 1.3,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "14px",
                      background: NAVY_GRADIENT,
                      boxShadow: "0 4px 14px rgba(30,58,138,0.3)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 100%)",
                        boxShadow: "0 6px 20px rgba(30,58,138,0.4)",
                        transform: "translateY(-1px)",
                      },
                      "&:disabled": {
                        background: "#E5E7EB",
                        boxShadow: "none",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    Preview Job
                  </Button>
                </Box>
              </>
            );
          })()}
        </form>

        {/* ══════════════════════════════════════
            Preview Dialog
        ══════════════════════════════════════ */}
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 25px 60px rgba(0,0,0,0.18)",
              maxHeight: "92vh",
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(15, 23, 42, 0.45)",
              backdropFilter: "blur(6px)",
            },
          }}
        >
          {/* Dialog Header */}
          <Box
            sx={{
              px: 3.5,
              py: 2.5,
              backgroundColor: "#FFFFFF",
              borderBottom: "1px solid #E5E7EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "14px",
                  backgroundColor: "#EFF6FF",
                  border: "1px solid #DBEAFE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 26, color: NAVY }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: NAVY, fontSize: "20px", lineHeight: 1.2 }}
                >
                  Preview Job Post
                </Typography>
                <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.3 }}>
                  Review all information before publishing.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  px: 2,
                  py: 0.7,
                  borderRadius: "20px",
                  backgroundColor: "#DCFCE7",
                  border: "1px solid #86EFAC",
                  color: "#166534",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  fontSize: "13px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontSize: "15px" }}>✓</span>
                Ready to Publish
              </Box>
              <IconButton
                onClick={() => setPreviewOpen(false)}
                size="small"
                sx={{
                  backgroundColor: "#F3F4F6",
                  width: 36,
                  height: 36,
                  "&:hover": { backgroundColor: "#E5E7EB" },
                }}
              >
                <CloseIcon sx={{ fontSize: 18, color: "#374151" }} />
              </IconButton>
            </Box>
          </Box>

          <DialogContent sx={{ p: 3, backgroundColor: "#F8FAFC" }}>

            {/* Job Summary Card */}
            <Paper
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                mb: 2.5,
                backgroundColor: "#FFFFFF",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "14px",
                    background: NAVY_GRADIENT,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "22px",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {formData.companyName ? formData.companyName[0].toUpperCase() : "J"}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#111827",
                      fontSize: "20px",
                      lineHeight: 1.25,
                    }}
                  >
                    {formData.title || "Job Title"}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mt: 0.8,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <BusinessOutlinedIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                      <Typography variant="body2" sx={{ color: "#4B5563", fontWeight: 500 }}>
                        {formData.companyName || "Company Name"}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: "#D1D5DB", fontSize: 12 }}>•</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <LocationOnOutlinedIcon sx={{ fontSize: 14, color: "#6B7280" }} />
                      <Typography variant="body2" sx={{ color: "#4B5563", fontWeight: 500 }}>
                        {formData.location || "Location"}
                      </Typography>
                    </Box>
                    {formData.workMode && (
                      <>
                        <Typography sx={{ color: "#D1D5DB", fontSize: 12 }}>•</Typography>
                        <Chip
                          label={formData.workMode.charAt(0).toUpperCase() + formData.workMode.slice(1)}
                          size="small"
                          sx={{
                            backgroundColor: "#EFF6FF",
                            color: NAVY,
                            fontWeight: 600,
                            height: "22px",
                            fontSize: "11px",
                            border: "1px solid #BFDBFE",
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* 2×2 Info Grid */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {[
                  {
                    label: "Work Mode",
                    value: formData.workMode
                      ? formData.workMode.charAt(0).toUpperCase() + formData.workMode.slice(1)
                      : "Not selected",
                    icon: <WorkOutlineOutlinedIcon sx={{ fontSize: 18 }} />,
                    bg: "#F0FDF4",
                    iconBg: "#DCFCE7",
                    color: "#166534",
                  },
                  {
                    label: "Salary Range",
                    value: formData.salary || "Not provided",
                    icon: <PaymentsOutlinedIcon sx={{ fontSize: 18 }} />,
                    bg: "#EFF6FF",
                    iconBg: "#DBEAFE",
                    color: "#1D4ED8",
                  },
                  {
                    label: "Experience",
                    value:
                      formData.minExperience !== "" && formData.maxExperience !== ""
                        ? `${formData.minExperience} – ${formData.maxExperience} Years`
                        : "Not selected",
                    icon: <WorkHistoryOutlinedIcon sx={{ fontSize: 18 }} />,
                    bg: "#FFF7ED",
                    iconBg: "#FED7AA",
                    color: "#C2410C",
                  },
                  {
                    label: "Stipend",
                    value: formData.stipend || "Not selected",
                    icon: <CardGiftcardOutlinedIcon sx={{ fontSize: 18 }} />,
                    bg: "#FDF4FF",
                    iconBg: "#F3E8FF",
                    color: "#1E3A8A",
                  },
                ].map((item, i) => (
                  <Grid item xs={6} key={i}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: "12px",
                        backgroundColor: item.bg,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "10px",
                          backgroundColor: item.iconBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: item.color,
                          flexShrink: 0,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{ color: "#6B7280", display: "block", fontWeight: 500 }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: item.color,
                            fontSize: "14px",
                            lineHeight: 1.3,
                            textTransform: "capitalize",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>

            {/* Job Description Card */}
            <Paper
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                mb: 2.5,
                backgroundColor: "#FFFFFF",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    backgroundColor: "#EFF6FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 18, color: NAVY }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "16px" }}>
                  Job Description
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: "10px",
                  backgroundColor: "#FAFAFA",
                  border: "1px solid #F3F4F6",
                  minHeight: 100,
                  color: "#374151",
                  lineHeight: 1.8,
                  fontSize: "14px",
                  "& p": { mb: 1, mt: 0 },
                  "& ul, & ol": { pl: 3, mt: 1, mb: 1 },
                  "& li": { mb: 0.5 },
                  "& h1, & h2, & h3": {
                    color: "#111827",
                    fontWeight: 600,
                    mb: 1,
                    mt: 1.5,
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: formData.description || "<p style='color:#9CA3AF'>No description added.</p>",
                }}
              />
            </Paper>

            {/* Requirements Card */}
            <Paper
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                mb: 2.5,
                backgroundColor: "#FFFFFF",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    backgroundColor: "#F0FDF4",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 18, color: "#166534" }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "16px" }}>
                  Requirements
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {formData.requirements ? (
                  formData.requirements
                    .split("\n")
                    .filter((r) => r.trim())
                    .map((req, i) => (
                      <Box
                        key={i}
                        sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                      >
                        <Box
                          sx={{
                            mt: "7px",
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            backgroundColor: NAVY,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: "#374151", lineHeight: 1.7 }}
                        >
                          {req}
                        </Typography>
                      </Box>
                    ))
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ color: "#9CA3AF", fontStyle: "italic" }}
                  >
                    No requirements added.
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Screening Questions Card */}
            <Paper
              sx={{
                p: 3,
                borderRadius: "16px",
                border: "1px solid #E5E7EB",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                mb: 2.5,
                backgroundColor: "#FFFFFF",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    backgroundColor: "#FFF7ED",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 18, color: "#C2410C" }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "16px" }}>
                  Screening Questions
                </Typography>
              </Box>

              {screeningQuestions.filter((q) => q.trim()).length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {screeningQuestions
                    .filter((q) => q.trim())
                    .map((question, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          alignItems: "flex-start",
                          p: 2,
                          borderRadius: "10px",
                          backgroundColor: "#F8FAFC",
                          border: "1px solid #F3F4F6",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: NAVY,
                            minWidth: 22,
                            fontSize: "14px",
                            lineHeight: 1.7,
                          }}
                        >
                          {i + 1}.
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#374151", lineHeight: 1.7 }}
                        >
                          {question}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: "10px",
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #F3F4F6",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#9CA3AF", fontStyle: "italic" }}
                  >
                    No screening questions added.
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Warning Banner */}
            <Box
              sx={{
                p: 2.5,
                borderRadius: "12px",
                backgroundColor: "#FFFBEB",
                border: "1px solid #FDE68A",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <InfoOutlinedIcon sx={{ color: "#D97706", fontSize: 20, flexShrink: 0, mt: 0.1 }} />
              <Typography variant="body2" sx={{ color: "#92400E", fontWeight: 500, lineHeight: 1.6 }}>
                Please review all details carefully before publishing. Once published, the job will be visible to candidates immediately.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3.5,
              py: 2.5,
              borderTop: "1px solid #E5E7EB",
              gap: 1.5,
              backgroundColor: "#FFFFFF",
            }}
          >
            <Button
              onClick={() => setPreviewOpen(false)}
              variant="outlined"
              sx={{
                borderRadius: "10px",
                px: 3,
                py: 1.1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                borderColor: "#E5E7EB",
                color: "#374151",
                "&:hover": {
                  borderColor: "#D1D5DB",
                  backgroundColor: "#F9FAFB",
                },
              }}
            >
              Back to Edit
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmPost}
              disabled={loading}
              sx={{
                borderRadius: "10px",
                px: 3.5,
                py: 1.1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                background: NAVY_GRADIENT,
                boxShadow: "0 4px 14px rgba(30,58,138,0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 100%)",
                  boxShadow: "0 6px 20px rgba(30,58,138,0.4)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": {
                  background: "#E5E7EB",
                  boxShadow: "none",
                  color: "#9CA3AF",
                },
                transition: "all 0.2s ease",
              }}
            >
              {loading ? "Publishing..." : "Publish Job"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ══════════════════════════════════════
            Limit Popup Dialog (preserved)
        ══════════════════════════════════════ */}
        <Dialog open={showLimitPopup} onClose={() => setShowLimitPopup(false)}>
          <DialogTitle>Plan Limit Reached</DialogTitle>
          <DialogContent>
            <Typography>{limitMessage}</Typography>
            <Typography sx={{ mt: 2 }}>
              Upgrade your subscription plan to continue.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowLimitPopup(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpgrade}>
              Upgrade Plan
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
}

export default PostJob;
